import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import {
  ClickAction,
  ClickTargetType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { removeNull } from '@diritto/common/utils/array';
import { ellipsis } from '@diritto/common/utils/string';
import { MyContentItemAction, MyContentItemActionText } from 'constants/studio';
import { useTrackingState } from 'contexts/TrackingProvider';
import { studioQueryKey } from 'queries/studio/queryKey';
import { useDeleteContentMutation } from 'queries/studio/useDeleteContentMutation';
import { MoreMenu } from 'ui-components/common/MoreMenu';
import { showAlertModal, showToast } from 'utils/modal';

interface MyContentMoreMenuProps {
  content: ContentPublicRelayDto;
  onDeleted?: () => void;
  disabled?: boolean;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.DirittoStudioContentsMoreMenu,
};

export const MyContentMoreMenu = ({ content, onDeleted, disabled }: MyContentMoreMenuProps) => {
  const { mutateAsync: deleteMutate } = useDeleteContentMutation(content.id);
  const { tracker } = useTrackingState();
  const queryClient = useQueryClient();
  const actions = useMemo(() => (disabled ? [] : removeNull([MyContentItemAction.DeleteMyContent])), [disabled]);

  const handleTrack = useCallback(
    (action: ClickAction, clickType: ClickTargetType) => {
      tracker.track(TrackingEventType.Click, { action, clickType, trackerExtraInfo });
    },
    [tracker],
  );

  const handleDelete = useCallback(async () => {
    try {
      if (content.id) {
        await deleteMutate();
        onDeleted?.();
        tracker.track(TrackingEventType.DeleteContent, { contentId: content.id, trackerExtraInfo });
        await queryClient.invalidateQueries({ queryKey: studioQueryKey.contents });
        showToast('작품이 삭제되었어요.');
      }
    } catch (e) {
      showAlertModal('삭제에 실패했어요.');
    }
  }, [content.id, deleteMutate, onDeleted, queryClient, tracker]);

  const handleAction = useCallback(
    async (action: MyContentItemAction) => {
      switch (action) {
        case MyContentItemAction.DeleteMyContent:
          handleTrack(ClickAction.DeleteContent, ClickTargetType.MoreMenu);
          tracker.track(TrackingEventType.ClickDeleteContent, { contentId: content.id, trackerExtraInfo });

          showAlertModal(
            {
              title: `<${ellipsis(content.title, 12)}> 작품을 삭제하시겠어요?`,
              description: '삭제 시, 영구적으로 삭제되며, 되돌릴 수 없어요.',
            },
            {
              cancelText: '취소',
              confirmText: '작품 삭제',
              onConfirm: handleDelete,
            },
          );
          return;
        default:
          console.warn('Undefined Action', action);
      }
    },
    [content.id, content.title, handleDelete, handleTrack, tracker],
  );

  const menu = useMemo(
    () =>
      actions.map(action => ({
        title: MyContentItemActionText[action] || '',
        onClick: () => handleAction(action),
      })),
    [actions, handleAction],
  );

  return <MoreMenu vertical menu={menu} disabled={disabled} />;
};
